import { getColor } from 'helpers';
import { CSSObject } from 'styled-components';

import { styled } from '../styled';
import { ColorsDark, ColorsLight } from '../theme';

import { TableStyleVariant, TableVariant } from './types';

interface Props {
  isCellClickable: boolean;
  overlayWrapperHeight?: number;
  variant?: TableVariant;
  styleVariant?: TableStyleVariant;
  isError?: boolean;
}

export const TableStyled = styled('div')<Props>(
  ({
    theme,
    isCellClickable,
    overlayWrapperHeight = 156,
    variant = 'standard',
    styleVariant = 'standard',
    isError,
  }) => ({
    width: '100%',
    position: 'relative',

    '.table-dataGrid': {
      borderRadius: 16,

      border:
        styleVariant === 'standard'
          ? `1px solid ${
              isError
                ? theme.palette.error.main
                : getColor(theme, {
                    dark: ColorsDark.gray100,
                    light: ColorsLight.gray150,
                  })
            }`
          : 'none',
      background: 'none',

      '.table-main': {
        overflow: 'unset',
      },

      '.table-virtualScroller': {
        marginTop: '0!important',
      },

      '.table-columnHeaders': {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        background:
          variant === 'standard'
            ? getColor(theme, {
                dark: ColorsDark.gray50,
                light: ColorsLight.gray50,
              })
            : '',
        height: 44,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderColor: getColor(theme, {
          dark: ColorsDark.gray100,
          light: ColorsLight.gray150,
        }),

        ...(styleVariant === 'outline' && {
          height: 38,
          borderRadius: 8,
          border: 0,
        }),

        '.table-columnHeaderTitle': {
          fontSize: styleVariant === 'standard' ? 12 : 14,
          fontWeight: styleVariant === 'standard' ? 600 : 500,
          color: theme.palette.text.gray,
        } as CSSObject,

        '.table-columnSeparator': {
          display: 'none',
        } as CSSObject,
      } as CSSObject,

      '.table-cell': {
        ...(styleVariant === 'outline' &&
          ({
            borderColor: getColor(theme, {
              dark: ColorsDark.gray200,
              light: ColorsLight.gray150,
            }),
          } as CSSObject)),
      },

      '.table-row:hover, .table-row.Mui-hovered': {
        cursor: isCellClickable ? 'pointer' : 'auto',
        backgroundColor: getColor(theme, {
          dark: ColorsDark.gray50,
          light: ColorsLight.gray50,
        }),
      },

      '.table-row-link': {
        color: 'inherit',
      },

      ...(styleVariant === 'standard' && {
        '.table-columnHeader:not(:first-child), .table-cell:not(:first-child)':
          {
            borderLeft: `1px solid  ${getColor(theme, {
              dark: ColorsDark.gray100,
              light: ColorsLight.gray150,
            })}`,
            borderColor: getColor(theme, {
              dark: ColorsDark.gray100,
              light: ColorsLight.gray150,
            }),
          } as CSSObject,
      }),

      ...(styleVariant === 'outline' && {
        '.MuiDataGrid-cell.MuiDataGrid-withBorderColor': {
          borderBottom: `1px solid  ${getColor(theme, {
            dark: ColorsDark.gray200,
            light: ColorsLight.gray150,
          })}`,
        },

        '.table-row--lastVisible .MuiDataGrid-cell.MuiDataGrid-withBorderColor':
          {
            borderBottom: 0,
          },
      }),

      '.table-columnHeader, .table-cell': {
        ...(styleVariant === 'standard' && {
          padding: variant === 'standard' ? '0 16px' : '0 12px',
        }),

        ...(styleVariant === 'outline' && {
          padding: '0 8px',
        }),
      } as CSSObject,

      '.table-columnHeader:focus, .table-cell:focus, .table-columnHeader:focus-within, .table-cell:focus-within':
        {
          outline: 'none',
        } as CSSObject,

      '.table-row--lastVisible .table-cell': {
        borderBottom: 0,
        minHeight: 70,
      } as CSSObject,
      '.table-row--lastVisible': {
        minHeight: 70,
        ...(styleVariant === 'standard' && {
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
        }),
      } as CSSObject,

      '.table-footerContainer': {
        borderColor: getColor(theme, {
          dark: ColorsDark.gray100,
          light: ColorsLight.gray150,
        }),
        height: 66,

        [theme.breakpoints.down('mobile')]: {
          justifyContent: 'center',
        },
      } as CSSObject,
    } as CSSObject,

    '.table-overlayWrapper': {
      minHeight: overlayWrapperHeight,
      ...theme.typography.bodyMedium,
      color: isError ? theme.palette.error.main : undefined,
    } as CSSObject,

    '.table-overlay': {
      background: 'none',
    },

    ...(styleVariant === 'outline' && {
      '.table-sort-down, .table-sort-up': {
        color: theme.palette.text.gray,
      },
    }),
  })
);
